import { logger } from "@/src/lib/logging/logger";

export interface RetryStrategy {
  shouldRetry: (error: any) => boolean;
  maxRetries: number;
}

export interface RetryDeadline {
  expiresAtMs: number;
  createError: () => Error;
}

const GENERIC_ERROR_CODES = [
  "40001", // serialization_failure
  "40P01", // deadlock_detected
  "-1", // network error
  "521", // Cloudflare origin down / Supabase transient outage
  "503", // service unavailable
  "PGRST003", // PostgREST connection pool timeout
  "ECONNRESET", // connection reset
  "ENOTFOUND", // DNS resolution failed
  "ETIMEDOUT", // connection timeout
  "ECONNREFUSED", // connection refused
  "OPTIMISTIC_LOCK_CONFLICT", // optimistic lock conflict
];

export const GENERIC_RETRY_STRATEGY: RetryStrategy = {
  shouldRetry: (error) =>
    GENERIC_ERROR_CODES.includes(error.code) ||
    error.message?.includes("connection timeout") ||
    error.message?.includes("fetch failed") ||
    error.message?.includes("network") ||
    error.message?.includes("ECONNRESET") ||
    error.message?.includes("ENOTFOUND") ||
    error.message?.includes("ETIMEDOUT") ||
    error.message?.includes("Network connection lost") ||
    error.message?.includes("Web server is down") ||
    error.message?.includes("Error code 521") ||
    error.message?.includes("gateway error"),
  maxRetries: 3,
};

export const ECW_FHIR_RETRY_STRATEGY: RetryStrategy = {
  shouldRetry: (error) =>
    GENERIC_RETRY_STRATEGY.shouldRetry(error) || [400, 408, 429, 500, 502, 503, 504].includes(error?.response?.status),
  maxRetries: 3,
};

export const ATHENA_RETRY_STRATEGY: RetryStrategy = {
  shouldRetry: (error) => error?.response?.status === 404 || error?.status === 404,
  maxRetries: 7,
};

export async function withRetry<T>(
  name: string,
  operation: () => Promise<{ data: T | null; error: any | null }>,
  { strategy = GENERIC_RETRY_STRATEGY, deadline }: { strategy?: RetryStrategy; deadline?: RetryDeadline } = {}
): Promise<{ data: T | null; error: any | null }> {
  const remainingMs = (): number | undefined =>
    deadline === undefined ? undefined : Math.max(0, deadline.expiresAtMs - Date.now());
  const assertWithinDeadline = (): number | undefined => {
    const remaining = remainingMs();

    if (remaining !== undefined && remaining <= 0) throw deadline!.createError();

    return remaining;
  };

  const handleBackoff = async (attempt: number, error: any) => {
    const backoffMs = exponentialBackoffSeconds(attempt, { baseSeconds: 2 }) * 1000;
    const remaining = assertWithinDeadline();

    if (remaining !== undefined && backoffMs >= remaining) throw deadline!.createError();

    logger.info(`Retrying ${name} in ${backoffMs}ms after error:`, { error });

    await new Promise((resolve) => setTimeout(resolve, backoffMs));
  };

  for (let attempt = 0; attempt < strategy.maxRetries; attempt++) {
    try {
      assertWithinDeadline();
      const result = await operation();

      if (result.error) {
        logger.warn(`Caught error while retrying operation ${name} in withRetry`, {
          error: result.error,
        });

        if (deadline && remainingMs() === 0) throw deadline.createError();

        if (!strategy.shouldRetry(result.error) || attempt === strategy.maxRetries - 1) {
          return result;
        }

        await handleBackoff(attempt, result.error);
        continue;
      }

      return result;
    } catch (error: any) {
      logger.warn(`Uncaught error while retrying operation ${name} in withRetry`, {
        error,
      });

      if (deadline && remainingMs() === 0) throw deadline.createError();

      if (!strategy.shouldRetry(error) || attempt === strategy.maxRetries - 1) {
        throw error;
      }

      await handleBackoff(attempt, error);
    }
  }

  return { data: null, error: new Error(`${name} failed after ${strategy.maxRetries} attempts`) };
}

export function exponentialBackoffSeconds(
  attempt: number,
  {
    baseSeconds = 10,
    capSeconds = 300,
    growthFactor = 2,
  }: { baseSeconds?: number; capSeconds?: number; growthFactor?: number } = {}
): number {
  const nominal = Math.min(capSeconds, baseSeconds * Math.pow(growthFactor, attempt));

  return Math.round(nominal * (0.5 + Math.random() * 0.5));
}
